import { map } from 'lodash-es'
import { ChangeEvent, FC, useState } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'

import { ICurrencyValues } from './CurrenciesTab'
import { Coin } from '../../../common/models'
import { IGroup } from '../../../common/models/groups'
import { FormUploadPreview } from '../../../core/components/FormUploadPreview'
import { deleteCoin } from '../../../core/services/currencies'
import { uploadImage } from '../../../core/services/upload-image'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormGroup } from '@/components/ui/form-group'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import { AlertRatio } from '@/components/AlertRatio'
import SelectField from '@/components/select-field'

type Props = {
  currency: Coin
  groups: IGroup[]
  control: Control<ICurrencyValues>
  errors: FieldErrors<ICurrencyValues>
  register: UseFormRegister<ICurrencyValues>
  index: number
  remove: UseFieldArrayRemove
  watch: UseFormWatch<ICurrencyValues>
  setValue: UseFormSetValue<ICurrencyValues>
  setCoins: (c: Coin[]) => void
  coins: Coin[]
}

export const Currency: FC<Props> = ({
  errors,
  currency,
  register,
  groups,
  index,
  setCoins,
  control,
  remove,
  watch,
  setValue,
  coins,
}): JSX.Element => {
  let iName = ''
  if (currency.iconLink) {
    const url = new URL(currency.iconLink)
    const i = url?.pathname?.lastIndexOf('/') + 1
    iName = url?.pathname?.substring(i)
  }
  const [imageName, setImageName] = useState<string>(iName)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [image, setImage] = useState<string>(currency.iconLink)
  const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false)
  const [deleteIndex, setDeleteIndex] = useState<number>()
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const name = watch(`currencies.${index}.name`)
  const id = watch(`currencies.${index}.id`)
  const festivalGroupId = watch(`currencies.${index}.festivalGroupId`)

  return (
    <>
      <Card className="mb-4">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="font-light">{name}</CardTitle>
            <Button
              variant="destructive"
              type="button"
              onClick={async () => {
                setDeleteIndex(index)
                setDeleteConfirmation(true)
              }}
            >
              Delete Currency
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <FormGroup
            label="Group Name"
            error={errors?.currencies?.[index]?.name?.message}
          >
            <Input
              isInvalid={!!errors?.currencies?.[index]?.name?.message}
              {...register(`currencies.${index}.name`)}
              type="text"
              placeholder="Currency Name"
            />
          </FormGroup>
          <FormUploadPreview
            formGroupProps={{
              className: 'mt-2',
            }}
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={
                  !!errors?.currencies?.[index]?.iconLink?.message ||
                  invalidPhoto ||
                  isErrorRatio
                }
                name={`currencies.${index}.icon`}
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const file = e?.target?.files?.[0]
                  if (file) {
                    if (file.size > 1_000_000) {
                      setInvalidPhoto(true)
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage('The photo is too large (1 MB)')
                      return
                    }
                    const data = await uploadImage(
                      file,
                      setIsUploading,
                      setIsError,
                      setShowMessageBox,
                      setMessage,
                      setIsErrorRatio,
                      'GROUP_CONFIG_CURRENCY',
                      false,
                    )
                    if (data.photoUrl) {
                      setImage(data.photoUrl)
                      setImageName(file.name)
                      setValue(`currencies.${index}.iconLink`, data.photoKey, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }
                }}
                type="file"
              />
            }
            imageName={imageName}
            direction="row"
            isUploading={isUploading}
            url={image}
            onRemove={() => {
              setImage('')
              setImageName('')
              setValue(`currencies.${index}.iconLink`, '')
            }}
            imageStyle={{
              objectFit: 'contain',
            }}
            label="Custom Currency Icon (64X64)"
            previewLabel="Custom Currency Icon (64X64)"
          />
          {isErrorRatio && <AlertRatio />}
          <Card className="mt-2 !bg-monochrome-3-60">
            <CardContent className="pt-6">
              <FormGroup
                label="Select Group"
                error={errors?.currencies?.[index]?.festivalGroupId?.message}
              >
                <Controller
                  name={`currencies.${index}.festivalGroupId`}
                  control={control}
                  render={({ field }) => (
                    <SelectField
                      outline
                      error={
                        errors?.currencies?.[index]?.festivalGroupId?.message
                      }
                      placeholder="Select Group"
                      options={map(groups, (group) => ({
                        value: group.id,
                        label: group.name,
                      }))}
                      value={field.value}
                      onValueChange={field.onChange}
                    />
                  )}
                />
              </FormGroup>
              <Card className="mt-2 !bg-monochrome-3-60">
                <CardContent className="pt-6">
                  <div className="flex w-full items-center justify-between gap-4">
                    <FormGroup
                      label="Custom Currency Amount"
                      tooltip="Custom Currency Amount"
                      className="flex-1"
                    >
                      <Input
                        value={1}
                        type="number"
                        disabled
                        placeholder="Custom Currency Amount"
                      />
                    </FormGroup>
                    <span className="mt-8">=</span>
                    <FormGroup
                      label="EUR (FESTIVAL GROUP MAIN CURRENCY)"
                      tooltip="EUR (FESTIVAL GROUP MAIN CURRENCY)"
                      error={errors?.currencies?.[index]?.exchangeRate?.message}
                      className="flex-1"
                    >
                      <Input
                        isInvalid={
                          !!errors?.currencies?.[index]?.exchangeRate?.message
                        }
                        {...register(`currencies.${index}.exchangeRate`)}
                        type="number"
                        placeholder="Enter Conversion Rate"
                      />
                    </FormGroup>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </CardContent>
        <CardFooter className="flex gap-2 justify-end">
          <Button variant="secondary" size="sm" type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            name={id}
            form={`currencies-${index}-form`}
            size="sm"
          >
            Save
          </Button>
        </CardFooter>
      </Card>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteConfirmation}
        setDeleteConfirmationModal={setDeleteConfirmation}
        deleteTitle={'Currency'}
        deleteFunc={async () => {
          if (id) {
            const resp = await deleteCoin(id)
            if (resp.status === 204) {
              setDeleteConfirmation(false)
            }
          }
          if (deleteIndex) {
            remove(deleteIndex)
            setCoins(coins.splice(deleteIndex, 1))
          }
        }}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
